import { Box, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { numericSx } from '../../../theme/typography';
import { avatarBarIndex } from '../../../utils/avatarHash';
import { CompanyAvatar } from '../../shared/CompanyAvatar';
import { PanelCard } from './PanelCard';
import type { DashboardCompany } from '../types';

export interface MyProjectRow {
  id: string;
  projectId: string;
  projectName: string;
  /** Optional client name. */
  clientName?: string | null;
  /** The project's company (branding) — renders a `CompanyAvatar` (Phase 8). */
  company?: DashboardCompany | null;
  /** Allocation summary line ("80% · until 29 May", "Rota · Sat / Sun"). */
  allocationSummary: string;
}

interface MyProjectsListProps {
  rows: MyProjectRow[];
  loading?: boolean;
  onOpenProject?: (projectId: string) => void;
}

/**
 * "My projects" row-list for the Allocations section — exact prototype
 * port of `MyProjects` (the v2 prototype
 * lines 330-350). Three-column flex row per project: 3-px coloured left
 * bar (`palette.bars[N]` via name-hash) · project name + client·alloc
 * summary · chevron-right.
 *
 * Rows show **current** allocations (start ≤ today ≤ end); the
 * AllocationsSection slices the `myAllocations` payload accordingly.
 */
export function MyProjectsList({
  rows,
  loading,
  onOpenProject,
}: MyProjectsListProps) {
  return (
    <PanelCard title="My projects" data-testid="widget-my-projects">
      {rows.length === 0 ? (
        <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem', py: 2 }}>
          {loading ? 'Loading…' : 'No current allocations.'}
        </Typography>
      ) : (
        <Stack spacing="10px">
          {rows.map((row) => (
            <ProjectRow key={row.id} row={row} onOpen={onOpenProject} />
          ))}
        </Stack>
      )}
    </PanelCard>
  );
}

function ProjectRow({
  row,
  onOpen,
}: {
  row: MyProjectRow;
  onOpen?: (projectId: string) => void;
}) {
  const barIdx = avatarBarIndex(row.projectName);
  const clickable = !!onOpen;
  return (
    <Box
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onOpen!(row.projectId) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen!(row.projectId);
              }
            }
          : undefined
      }
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        cursor: clickable ? 'pointer' : undefined,
        outline: 'none',
        '&:hover .proj-name': clickable ? { color: 'primary.main' } : {},
      }}
    >
      {row.company ? (
        <CompanyAvatar
          name={row.company.name}
          logoId={row.company.logoId}
          icon={row.company.icon}
          color={row.company.color}
          size={24}
          aria-hidden
        />
      ) : (
        <Box
          sx={(theme) => ({
            width: 3,
            alignSelf: 'stretch',
            minHeight: 32,
            backgroundColor: theme.palette.bars[barIdx].fill,
            borderRadius: '2px',
            flexShrink: 0,
          })}
        />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          className="proj-name"
          sx={{
            fontSize: '0.78125rem',
            fontWeight: 600,
            color: 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.3,
          }}
        >
          {row.projectName}
        </Typography>
        <Typography
          sx={[
            numericSx,
            {
              fontSize: '0.6875rem',
              color: 'text.disabled',
              lineHeight: 1.3,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          ]}
        >
          {row.clientName ? `${row.clientName} · ` : ''}
          {row.allocationSummary}
        </Typography>
      </Box>
      {clickable ? (
        <ChevronRightIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
      ) : null}
    </Box>
  );
}
